import { MutationWithContext } from '@lidea/shared/data-access/core';

/** Mutations where a slow/failed sync should offer offline mode. */
const OFFLINE_QUEUE_MUTATION_KEYS = new Set(['updateMeasure']);

/** Mutations where a long pending state may mean the device struggles to send data. */
const SYNC_STRUGGLE_MUTATION_KEYS = new Set([
  'updateMeasure',
  'uploadCropPicture',
]);

/** How long a sync mutation may stay pending before we suggest offline mode. */
const SLOW_MUTATION_THRESHOLD_MS = 7_000;

export function supportsOfflineQueue(
  mutationKey: readonly unknown[] | undefined
): boolean {
  const key = mutationKey?.[0];
  return typeof key === 'string' && OFFLINE_QUEUE_MUTATION_KEYS.has(key);
}

export function isSyncStruggleMutation(
  mutationKey: readonly unknown[] | undefined
): boolean {
  const key = mutationKey?.[0];
  return typeof key === 'string' && SYNC_STRUGGLE_MUTATION_KEYS.has(key);
}

export function getSlowMutationThresholdMs(
  mutation: MutationWithContext
): number | null {
  if (!isSyncStruggleMutation(mutation.options.mutationKey)) {
    return null;
  }

  return SLOW_MUTATION_THRESHOLD_MS;
}

export function isMutationStillStruggling(
  mutation: MutationWithContext
): boolean {
  if (mutation.state.status !== 'pending' || mutation.state.isPaused) {
    return false;
  }

  if (!isSyncStruggleMutation(mutation.options.mutationKey)) {
    return false;
  }

  const submittedAt = mutation.state.submittedAt;
  if (!submittedAt) {
    return false;
  }

  const threshold = getSlowMutationThresholdMs(mutation);
  if (threshold === null) {
    return false;
  }

  return Date.now() - submittedAt >= threshold;
}
